/* Native Shopify POST forms retain checkout, app properties, and digital delivery. */
(() => {
  if (window.bgPremiumInitialized) return;
  window.bgPremiumInitialized = true;
  const instances = new Map();
  const stickyInstances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function reservePurchaseSpace() {
    const height = Math.max(0, ...[...stickyInstances.keys()].filter(bar => bar.isConnected && !bar.hidden).map(bar => Math.ceil(bar.getBoundingClientRect().height)));
    document.body.classList.toggle('bgp-mobile-space', height > 0);
    document.documentElement.classList.toggle('bgp-mobile-space', height > 0);
    document.documentElement.style.setProperty('--bgp-sticky-height', `${height}px`);
  }
  function initializePurchaseBar(root) {
    root.querySelectorAll('[data-bgp-sticky]').forEach(bar => {
      if (stickyInstances.has(bar)) return;
      const controller = new AbortController();
      const mobile = matchMedia('(max-width: 749px)');
      const update = () => {
        const editing = document.activeElement?.matches('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]), textarea, [contenteditable="true"]');
        bar.hidden = !mobile.matches || editing;
        reservePurchaseSpace();
      };
      const on = (node, event, callback) => node.addEventListener(event, callback, {signal: controller.signal});
      on(mobile, 'change', update);
      on(document, 'focusin', update);
      on(document, 'focusout', () => requestAnimationFrame(() => {if (!controller.signal.aborted) update();}));
      const observer = new ResizeObserver(reservePurchaseSpace);
      observer.observe(bar);
      stickyInstances.set(bar, {update, destroy() {controller.abort();observer.disconnect();bar.hidden = true;}});
      update();
    });
  }
  function initializeVideoPosters(root) {
    root.querySelectorAll('[data-bgp-video-poster]').forEach(button => {
      if (button.dataset.ready) return;
      const frame = button.parentElement.querySelector('iframe');
      if (!frame) return;
      button.dataset.ready = 'true';button.hidden = false;frame.tabIndex = -1;
      button.addEventListener('click', () => {
        const url = new URL(frame.src);url.searchParams.set('autoplay', '1');frame.src = url.href;
        button.hidden = true;frame.removeAttribute('tabindex');frame.focus();
      }, {once: true});
    });
  }
  function initialize(root) {
    initializePurchaseBar(root);
    initializeVideoPosters(root);
    root.querySelectorAll('[data-bgp-product]').forEach(product => {
      if (instances.has(product)) return;
      const controller = new AbortController();
      const on = (node, event, fn) => node?.addEventListener(event, fn, {signal: controller.signal});
      const gallery = product.querySelector('[data-bgp-gallery]');
      const track = gallery?.querySelector('.bgp-media-track');
      const slides = [...(track?.children || [])];
      const thumbnails = [...(gallery?.querySelectorAll('[data-media-target]') || [])];
      let active = 0;
      function mark(index) {
        active = index;
        thumbnails.forEach((thumbnail, i) => i === index ? thumbnail.setAttribute('aria-current', 'true') : thumbnail.removeAttribute('aria-current'));
        slides.forEach((slide, i) => {
          slide.inert = i !== index;
          if (i === index) return;
          slide.querySelectorAll('video').forEach(video => video.pause());
          slide.querySelectorAll('iframe').forEach(frame => {
            if (!frame.contentWindow) return;
            const origin = new URL(frame.src).origin;
            frame.contentWindow.postMessage(JSON.stringify({event: 'command', func: 'pauseVideo', args: []}), origin);
            frame.contentWindow.postMessage(JSON.stringify({method: 'pause'}), origin);
          });
        });
      }
      function show(index, smooth = true) {
        if (!slides.length) return;
        index = (index + slides.length) % slides.length;
        mark(index);
        track.scrollTo({left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: smooth && !reduced.matches ? 'smooth' : 'instant'});
      }
      thumbnails.forEach((thumbnail, index) => on(thumbnail, 'click', event => {event.preventDefault();show(index);}));
      on(gallery?.querySelector('[data-gallery-prev]'), 'click', () => show(active - 1));
      on(gallery?.querySelector('[data-gallery-next]'), 'click', () => show(active + 1));
      on(track, 'keydown', event => {
        if (event.target !== track) return;
        if (!['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault();show(active + (event.key === 'ArrowRight' ? 1 : -1));
      });
      const observer = track ? new IntersectionObserver(entries => {
        entries.forEach(entry => {if (entry.isIntersecting && entry.intersectionRatio > .65) mark(slides.indexOf(entry.target));});
      }, {root: track, threshold: .65}) : null;
      slides.forEach(slide => observer.observe(slide));
      show(Math.max(0, slides.findIndex(slide => slide.hasAttribute('data-initial'))), false);
      const resizeObserver = track ? new ResizeObserver(() => show(active, false)) : null;
      if (track) resizeObserver.observe(track);
      const select = product.querySelector('[data-bgp-variant]');
      function sync(changeUrl = false) {
        const option = select?.selectedOptions[0];
        if (!option) return;
        const scopes = [product, ...document.querySelectorAll(`[data-bgp-cta][data-product-id="${product.dataset.productId}"]`)];
        const available = option.dataset.available === 'true';
        scopes.forEach(scope => {
          scope.querySelectorAll('[data-bgp-price]').forEach(price => {
            price.querySelector('[data-price]').innerHTML = option.dataset.price;
            const compare = price.querySelector('[data-compare]');
            compare.innerHTML = '<span class="bgp-sr">Regular price </span>' + option.dataset.compare;
            compare.hidden = option.dataset.sale !== 'true';
            const discount = price.querySelector('[data-discount]');
            discount.textContent = option.dataset.discount + '% OFF';
            discount.hidden = option.dataset.sale !== 'true';
          });
          scope.querySelectorAll('[data-bgp-add], [data-bgp-buy]').forEach(button => {button.disabled = !available;});
          scope.querySelectorAll('[data-add-label]').forEach(label => {label.textContent = available ? label.dataset.availableLabel : label.dataset.unavailableLabel;});
          scope.querySelectorAll('[data-bgp-cta-variant]').forEach(input => {input.value = option.value;});
        });
        const accelerated = product.querySelector('.bgp-accelerated');
        if (accelerated) accelerated.hidden = !available;
        if (changeUrl) {
          const url = new URL(location.href);url.searchParams.set('variant', option.value);history.replaceState({}, '', url);
          const mediaIndex = slides.findIndex(slide => slide.dataset.mediaId === option.dataset.media);
          if (mediaIndex >= 0) show(mediaIndex);
        }
      }
      on(select, 'change', () => sync(true));
      sync();
      instances.set(product, {sync, destroy() {controller.abort();observer?.disconnect();resizeObserver?.disconnect();}});
    });
    instances.forEach(instance => instance.sync());
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, product) => {if (event.target.contains(product)) {instance.destroy();instances.delete(product);}});
    stickyInstances.forEach((instance, bar) => {if (event.target.contains(bar)) {instance.destroy();stickyInstances.delete(bar);}});
    reservePurchaseSpace();
  });
  window.addEventListener('pageshow', () => {instances.forEach(instance => instance.sync());stickyInstances.forEach(instance => instance.update());});
})();
